import React, { useState } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
  Typography,
  TextField,
  ListSubheader
} from '@mui/material';
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PublicIcon from '@mui/icons-material/Public';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { 
  TIMEZONE_LIST,
  formatTimezoneDisplay,
  getCurrentTimezone
} from '../utils/dateFormat';

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);

interface DateTimePickerWithTimezoneProps {
  value: any;
  onChange: (value: any) => void;
  includeTime?: boolean;
  showTimezone?: boolean;
  error?: boolean;
  helperText?: string;
  fullWidth?: boolean;
  label?: string;
  required?: boolean;
}

const DateTimePickerWithTimezone: React.FC<DateTimePickerWithTimezoneProps> = ({
  value,
  onChange,
  includeTime = false,
  showTimezone = false,
  error = false,
  helperText = '',
  fullWidth = true,
  label,
  required = false
}) => {
  // Parse existing value
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(() => {
    if (!value) return null;
    
    // Handle new format with timezone
    if (value && typeof value === 'object' && value.date) {
      return dayjs(value.date);
    }
    
    // Handle legacy format (just date string)
    if (typeof value === 'string' || value instanceof Date) {
      return dayjs(value);
    }
    
    return null;
  });

  const [selectedTimezone, setSelectedTimezone] = useState<string>(() => {
    // Get timezone from existing value or use browser timezone as default
    if (value && typeof value === 'object' && value.timezone) {
      return value.timezone;
    }
    // Default to browser timezone for better user experience
    return getCurrentTimezone();
  });

  const [openCalendar, setOpenCalendar] = useState(false);
  const [timezoneSearchTerm, setTimezoneSearchTerm] = useState('');

  // Update internal state when value prop changes
  React.useEffect(() => {
    if (!value) {
      setSelectedDate(null);
      setSelectedTimezone(getCurrentTimezone());
      return;
    }
    
    // Handle new format with timezone
    if (typeof value === 'object' && value.date) {
      setSelectedDate(dayjs(value.date));
      if (value.timezone) {
        setSelectedTimezone(value.timezone);
      }
    } else if (typeof value === 'string' || value instanceof Date || dayjs.isDayjs(value)) {
      // Handle legacy format
      setSelectedDate(dayjs(value));
    }
  }, [value]);

  const handleDateChange = (newDate: Dayjs | null) => {
    setSelectedDate(newDate);
    updateValue(newDate, selectedTimezone);
  };

  const handleTimezoneChange = (newTimezone: string) => {
    setSelectedTimezone(newTimezone);
    updateValue(selectedDate, newTimezone);
  };

  const updateValue = (date: Dayjs | null, tz: string) => {
    if (!date) {
      onChange(null);
      return;
    }

    // Pass the selected value exactly as it is without timezone conversions
    // Store date with or without time based on includeTime prop
    const dateValue = {
      date: includeTime 
        ? date.format('YYYY-MM-DDTHH:mm:ss') // Store full datetime when time is required
        : date.format('YYYY-MM-DD'), // Store date only when time is not required
      timezone: tz,
      timestamp: date.valueOf(),
      timezoneDisplay: formatTimezoneDisplay(tz, date.toDate()),
      includeTime: includeTime, // Store this flag to help with display formatting
      includeTimezone: showTimezone // Store this flag to help with display formatting
    };
    
    onChange(dateValue);
  };

  const PickerComponent = includeTime ? DateTimePicker : DatePicker;

  // Filter timezone list based on search term
  const filteredTimezones = TIMEZONE_LIST.filter(tz => 
    tz.label.toLowerCase().includes(timezoneSearchTerm.toLowerCase()) ||
    tz.value.toLowerCase().includes(timezoneSearchTerm.toLowerCase())
  );

  return (
    <Box>
      {/* Date/DateTime Picker - Full width on separate line */}
      <Box sx={{ mb: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <PickerComponent
            label={label}
            value={selectedDate}
            onChange={handleDateChange}
            open={openCalendar}
            onOpen={() => setOpenCalendar(true)}
            onClose={() => setOpenCalendar(false)}
            slotProps={{
              textField: {
                fullWidth: fullWidth,
                variant: 'outlined',
                sx: {
                  '& .MuiInputBase-input': {
                    padding: "8px"
                  }
                },
                InputProps: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setOpenCalendar(true)}>
                        {includeTime ? <AccessTimeIcon /> : <CalendarMonthIcon />}
                      </IconButton>
                    </InputAdornment>
                  )
                }
              }
            }}
          />
        </LocalizationProvider>
      </Box>

      {/* Timezone Selector - Full width on separate line */}
      {showTimezone && (
      <Box>
        <FormControl fullWidth variant="outlined">
          <InputLabel id="timezone-select-label">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PublicIcon fontSize="small" />
              Timezone
            </Box>
          </InputLabel>
          <Select
            labelId="timezone-select-label"
            value={selectedTimezone}
            onChange={(e) => handleTimezoneChange(e.target.value)}
            onClose={() => setTimezoneSearchTerm('')}
            label="Timezone"
            renderValue={(value) => {
              const currentDate = selectedDate ? selectedDate.toDate() : new Date();
              const timezoneDisplay = formatTimezoneDisplay(value as string, currentDate);
              const selectedTz = TIMEZONE_LIST.find(tz => tz.value === value);
              const displayText = selectedTz ? `${selectedTz.label} - ${timezoneDisplay}` : value;
              // Truncate if too long to prevent overflow
              return displayText.length > 45 ? `${displayText.substring(0, 42)}...` : displayText;
            }}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 400,
                  maxWidth: 400,
                  minWidth: 320
                }
              }
            }}
            sx={{
              '& .MuiSelect-select': {
                padding: "8px 14px",
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }
            }}
          >
            <ListSubheader sx={{ pb: 1, pt: 1 }}>
              <TextField
                size="small"
                autoFocus
                placeholder="Search timezones..."
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PublicIcon fontSize="small" />
                    </InputAdornment>
                  )
                }}
                value={timezoneSearchTerm}
                onChange={(e) => setTimezoneSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'Escape') {
                    // Prevent the select from closing when typing
                    e.stopPropagation();
                  }
                }}
                sx={{ 
                  mb: 0.5,
                  '& .MuiInputBase-input': {
                    padding: '8px 12px'
                  }
                }}
              />
            </ListSubheader>
            {filteredTimezones.map((tz) => {
              const currentDate = selectedDate ? selectedDate.toDate() : new Date();
              const timezoneDisplay = formatTimezoneDisplay(tz.value, currentDate);
              
              return (
                <MenuItem key={tz.value} value={tz.value} sx={{ maxWidth: '100%' }}>
                  <Box sx={{ width: '100%', overflow: 'hidden' }}>
                    <Typography variant="body2" noWrap>
                      {tz.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {timezoneDisplay}
                    </Typography>
                  </Box>
                </MenuItem>
              );
            })}
            {filteredTimezones.length === 0 && (
              <MenuItem disabled>
                <Typography variant="body2" color="text.secondary">
                  No timezones found matching "{timezoneSearchTerm}"
                </Typography>
              </MenuItem>
            )}
          </Select>
        </FormControl>
      </Box>
      )}

      {/* Error message only */}
      {error && helperText && (
        <Typography variant="caption" color="error" sx={{ display: 'block' }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

export default DateTimePickerWithTimezone;
